<script lang="ts">
  import type {
    LegalLicense,
    LegalLicensesPage
  } from '$lib/sanity/schemas/entities/LegalLicensesPage';
  import PortableText from '$components/SanityComponents/PortableText/PortableText.svelte';
  import { BGMode } from '$components/News/Background/BackgroundGradient.svelte';
  import Container from '$components/News/Container.svelte';
  import { slugify } from '$lib/utils/slugify';
  import { ResponsiveVars, TwistGrid, TwistGridItem } from '@twist/core';
  import LegalPageHeader from '../LegalPageHeader.svelte';
  import LicensesTable from './LicensesTable.svelte';
  import LicensesJurisdictionInfoList from './LicensesJurisdictionInfoList.svelte';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { useEncodeDataAttribute, type ContentSourceMap } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  export let document: LegalLicensesPage;
  export let sourceMap: ContentSourceMap | undefined = undefined;

  $: encodeDataAttribute = useEncodeDataAttribute(document, sourceMap, sanityStudioURL);

  function handleJurisdictionClick(e: CustomEvent<{ license: LegalLicense }>) {
    const anchorElement = window.document.getElementById(slugify(e.detail.license.jurisdiction));

    if (anchorElement) {
      const { top } = anchorElement.getBoundingClientRect();
      window.scrollTo({ top: top + window.scrollY, behavior: 'smooth' });
    }
  }

  const responsiveVars = new ResponsiveVars()
    .addFluid('font-size', toFluidVarInput(20, 36))
    .addFluid('mb', toFluidVarInput(40, 60))
    .toCSS();
</script>

<LegalPageHeader title="Licenses" bg={document.colors.primary} />

<div
  data-background
  data-bgmode={BGMode.DOUBLE}
  data-bgcolor1={document.colors.secondary}
  data-bgcolor2={document.colors.primary}
>
  <Container>
    <TwistGrid
      rowGaps={[
        ['min', 64],
        ['sm', 'lg', 64, 128],
        ['lg', 'max', 56, 112],
        ['max', 112]
      ]}
    >
      <TwistGridItem colSpan={{ min: 4, lg: 9 }} colStart={{ min: 1, lg: 3 }} rowStart={1}>
        {#if document.intro}
          <div data-sanity={encodeDataAttribute(['intro'])}>
            <PortableText body={document.intro} />
          </div>
        {/if}
      </TwistGridItem>

      <TwistGridItem colSpan={{ min: 4, lg: 9 }} colStart={{ min: 1, lg: 3 }} rowStart={2}>
        {#if document.licenses}
          <LicensesTable
            licenses={document.licenses}
            on:jurisdictionClick={handleJurisdictionClick}
            path={['licenses']}
            {sourceMap}
          />
        {/if}
      </TwistGridItem>

      <TwistGridItem colSpan={{ min: 4, lg: 9 }} colStart={{ min: 1, lg: 3 }} rowStart={3}>
        {#if document.postTableNotes}
          <div data-sanity={encodeDataAttribute(['postTableNotes'])}>
            <PortableText body={document.postTableNotes} />
          </div>
        {/if}
      </TwistGridItem>

      <TwistGridItem colSpan={{ min: 4, lg: 9 }} colStart={{ min: 1, lg: 3 }} rowStart={4}>
        {#if document.jurisdictionInfoList}
          <LicensesJurisdictionInfoList
            jurisdictionInfoList={document.jurisdictionInfoList}
            path={['jurisdictionInfoList']}
            {sourceMap}
          />
        {/if}

        {#if document.payrollReceiptInformation}
          <h3 class={responsiveVars}>Payroll Receipt Information</h3>

          <LicensesJurisdictionInfoList
            jurisdictionInfoList={document.payrollReceiptInformation}
            path={['payrollReceiptInformation']}
            {sourceMap}
          />
        {/if}
      </TwistGridItem>
    </TwistGrid>
  </Container>
</div>

<style lang="scss">
  h3 {
    font-family: var(--font-family-primary);
    font-size: var(--font-size);
    line-height: 115%;
    margin-bottom: var(--mb);
  }
</style>
